import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  X,
  Plus,
  Trash2,
  Check,
  Clock,
  User,
  AlertTriangle,
  CheckCircle,
  Circle,
  Download,
  Send,
  Edit,
  MapPin,
  Calendar,
  Flag,
  FileText,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/firebase/api';

interface ShiftHandoffProps {
  currentUserId: string;
  currentUserName: string;
  wells: any[];
  onClose: () => void;
}

export default function ShiftHandoff({
  currentUserId,
  currentUserName,
  wells,
  onClose,
}: ShiftHandoffProps) {
  const [handoff, setHandoff] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modals
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddIssue, setShowAddIssue] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  
  // Form states
  const [notes, setNotes] = useState('');
  const [selectedShift, setSelectedShift] = useState('day');
  const [toUserName, setToUserName] = useState('');
  
  // History
  const [showHistory, setShowHistory] = useState(false);
  const [historyHandoffs, setHistoryHandoffs] = useState([]);

  useEffect(() => {
    loadActiveHandoff();
    loadHistory();
  }, []);

  const loadActiveHandoff = async () => {
    try {
      const activeHandoff = await api.getActiveHandoff(currentUserId);
      
      if (activeHandoff) {
        // Continue existing handoff
        setHandoff(activeHandoff);
        setNotes(activeHandoff.notes || '');
        setSelectedShift(activeHandoff.shift || 'day');
        setToUserName(activeHandoff.toUserName || '');
      } else {
        // Create new handoff
        await createNewHandoff();
      }
    } catch (error) {
      console.error('Error loading active handoff:', error);
      await createNewHandoff();
    } finally {
      setLoading(false);
    }
  };

  const createNewHandoff = async () => {
    try {
      const newHandoff = {
        fromUserId: currentUserId,
        fromUserName: currentUserName,
        shift: 'day',
        tasks: [],
        issues: [],
        notes: '',
        wellsChecked: [],
        wellsNeedingAttention: [],
      };

      const created = await api.createShiftHandoff(newHandoff);
      if (created) {
        setHandoff(created);
      }
    } catch (error) {
      console.error('Error creating handoff:', error);
      toast.error('Failed to create shift handoff');
    }
  };

  const loadHistory = async () => {
    try {
      const allHandoffs = await api.fetchShiftHandoffs();
      setHistoryHandoffs(allHandoffs.filter(h => h.submitted));
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const saveHandoff = async (updates: any) => {
    if (!handoff) return;

    setSaving(true);
    try {
      const updated = await api.updateShiftHandoff(handoff.id, updates);
      if (updated) {
        setHandoff(updated);
        return updated;
      }
    } catch (error) {
      console.error('Error saving handoff:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const addTask = async (taskData: any) => {
    try {
      // Create task in database
      const task = {
        ...taskData,
        handoffId: handoff.id,
        assignedBy: currentUserId,
        assignedByName: currentUserName,
      };

      const createdTask = await api.createTask(task);
      
      if (createdTask) {
        // Add to handoff
        const updatedTasks = [...(handoff.tasks || []), createdTask.id];
        await saveHandoff({ tasks: updatedTasks });
        
        // Create notification if task is assigned
        if (createdTask.assignedTo && createdTask.assignedTo !== currentUserId) {
          await createTaskNotification(createdTask, 'assigned');
        }

        toast.success('Task added successfully');
        setShowAddTask(false);
        loadActiveHandoff();
      }
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error('Failed to add task');
    }
  };

  const updateTask = async (taskId: string, updates: any) => {
    try {
      const updatedTask = await api.updateTask(taskId, updates);
      
      if (updatedTask) {
        // Create notification for status changes
        if (updates.status) {
          await createTaskNotification(updatedTask, updates.status);
        }

        toast.success('Task updated');
        loadActiveHandoff();
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      await api.deleteTask(taskId);
      
      // Remove from handoff
      const updatedTasks = (handoff.tasks || []).filter(id => id !== taskId);
      await saveHandoff({ tasks: updatedTasks });
      
      toast.success('Task deleted');
      loadActiveHandoff();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const createTaskNotification = async (task: any, action: string) => {
    if (!(window as any).createNotification) return;

    let title = '';
    let message = '';

    if (action === 'assigned') {
      title = `New Task Assigned: ${task.title}`;
      message = `${task.assignedByName} assigned you a ${task.priority} priority task`;
    } else if (action === 'completed') {
      title = `Task Completed: ${task.title}`;
      message = `Task has been marked as completed`;
    } else if (action === 'in-progress') {
      title = `Task In Progress: ${task.title}`;
      message = `Work has started on this task`;
    }

    await (window as any).createNotification({
      type: action === 'completed' ? 'success' : 'info',
      title,
      message,
      wellId: task.wellId,
    });
  };

  const addIssue = async (issueData: any) => {
    const updatedIssues = [...(handoff.issues || []), issueData];
    await saveHandoff({ issues: updatedIssues });
    toast.success('Issue added');
    setShowAddIssue(false);
  };

  const deleteIssue = async (index: number) => {
    const updatedIssues = (handoff.issues || []).filter((_, i) => i !== index);
    await saveHandoff({ issues: updatedIssues });
    toast.success('Issue removed');
  };

  const submitHandoff = async () => {
    if (!handoff) return;

    // Validation
    if (!toUserName.trim()) {
      toast.error('Please specify who you are handing off to');
      return;
    }

    try {
      await api.submitShiftHandoff(handoff.id, {
        toUserName,
        notes,
        shift: selectedShift,
      });
      
      // Create notification for incoming shift
      if ((window as any).createNotification) {
        await (window as any).createNotification({
          type: 'info',
          title: 'Shift Handoff Received',
          message: `${currentUserName} has handed off their ${selectedShift} shift to you`,
          forAdmin: false,
        });
      }

      toast.success('Shift handoff submitted successfully!');
      onClose();
    } catch (error) {
      console.error('Error submitting handoff:', error);
      toast.error('Failed to submit handoff');
    }
  };

  const exportPDF = async () => {
    toast.info('Generating PDF...', { description: 'Please wait' });

    // In a real implementation, use jsPDF or similar
    // For now, create a printable view
    const printContent = `
      SHIFT HANDOFF REPORT
      =====================
      
      From: ${currentUserName}
      To: ${toUserName || 'Next Shift'}
      Shift: ${selectedShift.toUpperCase()}
      Date: ${new Date().toLocaleDateString()}
      Time: ${new Date().toLocaleTimeString()}
      
      TASKS (${handoff?.tasks?.length || 0})
      -----------------
      ${(handoff?.tasks || []).map((t, i) => `${i + 1}. ${t.title} - ${t.status}`).join('\n')}
      
      ISSUES (${handoff?.issues?.length || 0})
      -----------------
      ${(handoff?.issues || []).map((issue, i) => `${i + 1}. ${issue.title}\n   ${issue.description}`).join('\n\n')}
      
      NOTES
      -----------------
      ${notes}
    `;

    // Create blob and download
    const blob = new Blob([printContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shift-handoff-${new Date().getTime()}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success('Handoff report exported!');
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
        <Card className="p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-3"></div>
            <p className="text-gray-700">Loading shift handoff...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex-shrink-0">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                  <FileText className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Shift Handoff</h2>
                  <p className="text-blue-100 text-sm">Pass critical information to next shift</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => setShowHistory(!showHistory)}
                  variant="outline"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  <Clock className="h-4 w-4 mr-2" />
                  History
                </Button>
                <Button
                  onClick={onClose}
                  variant="ghost"
                  className="h-10 w-10 p-0 hover:bg-white/20 text-white"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </div>

            {/* Handoff Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/10 rounded-lg p-3">
                <label className="text-xs text-blue-100 block mb-1">From</label>
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4" />
                  <span className="font-medium">{currentUserName}</span>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-3">
                <label className="text-xs text-blue-100 block mb-1">To</label>
                <Input
                  value={toUserName}
                  onChange={(e) => {
                    setToUserName(e.target.value);
                    saveHandoff({ toUserName: e.target.value });
                  }}
                  placeholder="Next shift person..."
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60 h-8"
                />
              </div>

              <div className="bg-white/10 rounded-lg p-3">
                <label className="text-xs text-blue-100 block mb-1">Shift</label>
                <select
                  value={selectedShift}
                  onChange={(e) => {
                    setSelectedShift(e.target.value);
                    saveHandoff({ shift: e.target.value });
                  }}
                  className="w-full bg-white/20 border border-white/30 rounded-lg px-3 py-1.5 text-white text-sm"
                >
                  <option value="day">Day Shift</option>
                  <option value="night">Night Shift</option>
                  <option value="swing">Swing Shift</option>
                </select>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {showHistory ? (
              <HistoryView handoffs={historyHandoffs} wells={wells} />
            ) : (
              <div className="space-y-6">
                {/* Tasks Section */}
                <TasksSection
                  handoff={handoff}
                  onAddTask={() => setShowAddTask(true)}
                  onUpdateTask={updateTask}
                  onDeleteTask={deleteTask}
                  wells={wells}
                />

                {/* Issues Section */}
                <IssuesSection
                  issues={handoff?.issues || []}
                  onAddIssue={() => setShowAddIssue(true)}
                  onDeleteIssue={deleteIssue}
                  wells={wells}
                />

                {/* Notes Section */}
                <Card className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-gray-600" />
                    Shift Notes
                  </h3>
                  <Textarea
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      saveHandoff({ notes: e.target.value });
                    }}
                    placeholder="Add any important notes for the next shift (e.g., weather conditions, equipment status, upcoming maintenance)..."
                    rows={6}
                    className="resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    {saving ? 'Saving...' : 'Auto-saved to database'}
                  </p>
                </Card>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          {!showHistory && (
            <div className="border-t bg-gray-50 p-6 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={exportPDF}
                    variant="outline"
                    className="h-12"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export PDF
                  </Button>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    onClick={onClose}
                    variant="outline"
                    className="h-12"
                  >
                    Save Draft
                  </Button>
                  <Button
                    onClick={submitHandoff}
                    className="h-12 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Submit Handoff
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Task Modal */}
      {showAddTask && (
        <AddTaskModal
          onClose={() => setShowAddTask(false)}
          onAdd={addTask}
          wells={wells}
          currentUserId={currentUserId}
        />
      )}

      {/* Add Issue Modal */}
      {showAddIssue && (
        <AddIssueModal
          onClose={() => setShowAddIssue(false)}
          onAdd={addIssue}
          wells={wells}
        />
      )}
    </>
  );
}

// ==================== SUB-COMPONENTS ====================

function TasksSection({ handoff, onAddTask, onUpdateTask, onDeleteTask, wells }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTasks();
  }, [handoff]);

  const loadTasks = async () => {
    if (!handoff?.tasks || handoff.tasks.length === 0) {
      setTasks([]);
      setLoading(false);
      return;
    }

    try {
      const allTasks = await api.fetchTasks();
      const handoffTasks = allTasks.filter(t => handoff.tasks.includes(t.id));
      setTasks(handoffTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'in-progress': return <Clock className="h-5 w-5 text-blue-600" />;
      default: return <Circle className="h-5 w-5 text-gray-400" />;
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center">
          <CheckCircle className="h-5 w-5 mr-2 text-gray-600" />
          Tasks
          <Badge className="ml-2 bg-blue-100 text-blue-800">
            {tasks.length}
          </Badge>
        </h3>
        <Button onClick={onAddTask} size="sm" className="h-9">
          <Plus className="h-4 w-4 mr-1" />
          Add Task
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading tasks...</div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No tasks yet. Click "Add Task" to create one.
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => (
            <div
              key={task.id}
              className="border rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    {getStatusIcon(task.status)}
                    <h4 className="font-medium text-gray-900">{task.title}</h4>
                    <Badge className={getPriorityColor(task.priority)}>
                      {task.priority}
                    </Badge>
                  </div>
                  {task.description && (
                    <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                  )}
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    {task.assignedToName && (
                      <span className="flex items-center space-x-1">
                        <User className="h-3 w-3" />
                        <span>{task.assignedToName}</span>
                      </span>
                    )}
                    {task.wellId && (
                      <span className="flex items-center space-x-1">
                        <MapPin className="h-3 w-3" />
                        <span>{wells.find(w => w.id === task.wellId)?.name || task.wellId}</span>
                      </span>
                    )}
                    {task.dueDate && (
                      <span className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <select
                    value={task.status}
                    onChange={(e) => onUpdateTask(task.id, { status: e.target.value })}
                    className="text-sm border rounded px-2 py-1"
                  >
                    <option value="todo">To Do</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                  <Button
                    onClick={() => onDeleteTask(task.id)}
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function IssuesSection({ issues, onAddIssue, onDeleteIssue, wells }) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
          Issues & Concerns
          <Badge className="ml-2 bg-orange-100 text-orange-800">
            {issues.length}
          </Badge>
        </h3>
        <Button onClick={onAddIssue} size="sm" className="h-9 bg-orange-600 hover:bg-orange-700">
          <Plus className="h-4 w-4 mr-1" />
          Add Issue
        </Button>
      </div>

      {issues.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No issues reported. Click "Add Issue" to report one.
        </div>
      ) : (
        <div className="space-y-3">
          {issues.map((issue, index) => (
            <div
              key={index}
              className="border-l-4 border-orange-500 bg-orange-50 rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">{issue.title}</h4>
                  <p className="text-sm text-gray-700 mb-2">{issue.description}</p>
                  {issue.wellId && (
                    <div className="flex items-center space-x-1 text-xs text-gray-600">
                      <MapPin className="h-3 w-3" />
                      <span>{wells.find(w => w.id === issue.wellId)?.name || issue.wellId}</span>
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => onDeleteIssue(index)}
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AddTaskModal({ onClose, onAdd, wells, currentUserId }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [assignedTo, setAssignedTo] = useState('');
  const [assignedToName, setAssignedToName] = useState('');
  const [wellId, setWellId] = useState('');
  const [dueDate, setDueDate] = useState('');

  const handleSubmit = () => {
    if (!title.trim()) {
      toast.error('Task title is required');
      return;
    }

    onAdd({
      title,
      description,
      priority,
      assignedTo: assignedTo || null,
      assignedToName: assignedToName || null,
      wellId: wellId || null,
      dueDate: dueDate || null,
    });

    // Reset form
    setTitle('');
    setDescription('');
    setPriority('medium');
    setAssignedTo('');
    setAssignedToName('');
    setWellId('');
    setDueDate('');
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Add New Task</h3>
            <Button onClick={onClose} variant="ghost" size="sm" className="h-8 w-8 p-0">
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Check pressure on Well 005"
                className="h-12"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Additional details about this task..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2.5"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date
                </label>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="h-11"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assign To
              </label>
              <Input
                value={assignedToName}
                onChange={(e) => setAssignedToName(e.target.value)}
                placeholder="Person responsible for this task"
                className="h-12"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Related Well (Optional)
              </label>
              <select
                value={wellId}
                onChange={(e) => setWellId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2.5"
              >
                <option value="">None</option>
                {wells.map((well) => (
                  <option key={well.id} value={well.id}>
                    {well.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t">
            <Button onClick={onClose} variant="outline" className="h-11">
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="h-11">
              <Plus className="h-4 w-4 mr-2" />
              Add Task
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function AddIssueModal({ onClose, onAdd, wells }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [wellId, setWellId] = useState('');

  const handleSubmit = () => {
    if (!title.trim()) {
      toast.error('Issue title is required');
      return;
    }

    onAdd({
      title,
      description,
      wellId: wellId || null,
      reportedAt: new Date().toISOString(),
    });

    setTitle('');
    setDescription('');
    setWellId('');
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Report Issue</h3>
            <Button onClick={onClose} variant="ghost" size="sm" className="h-8 w-8 p-0">
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Issue Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Pressure drop on Well 003"
                className="h-12"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue in detail..."
                rows={5}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Related Well (Optional)
              </label>
              <select
                value={wellId}
                onChange={(e) => setWellId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2.5"
              >
                <option value="">None</option>
                {wells.map((well) => (
                  <option key={well.id} value={well.id}>
                    {well.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t">
            <Button onClick={onClose} variant="outline" className="h-11">
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="h-11 bg-orange-600 hover:bg-orange-700">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Report Issue
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function HistoryView({ handoffs, wells }) {
  const [expandedId, setExpandedId] = useState(null);

  if (handoffs.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600">No handoff history yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {handoffs.map((handoff) => (
        <Card key={handoff.id} className="overflow-hidden">
          <div
            className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
            onClick={() => setExpandedId(expandedId === handoff.id ? null : handoff.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-4 mb-2">
                  <Badge className="bg-blue-100 text-blue-800">
                    {handoff.shift} shift
                  </Badge>
                  <span className="text-sm text-gray-600">
                    {new Date(handoff.submittedAt).toLocaleDateString()} at{' '}
                    {new Date(handoff.submittedAt).toLocaleTimeString()}
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-700">
                  <span>From: <strong>{handoff.fromUserName}</strong></span>
                  <span>→</span>
                  <span>To: <strong>{handoff.toUserName}</strong></span>
                </div>
              </div>
              {expandedId === handoff.id ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </div>

          {expandedId === handoff.id && (
            <div className="p-6 border-t space-y-4">
              {handoff.tasks && handoff.tasks.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Tasks ({handoff.tasks.length})</h4>
                  <div className="space-y-2">
                    {handoff.tasks.map((taskId, i) => (
                      <div key={i} className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                        Task ID: {taskId}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {handoff.issues && handoff.issues.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Issues ({handoff.issues.length})</h4>
                  <div className="space-y-2">
                    {handoff.issues.map((issue, i) => (
                      <div key={i} className="bg-orange-50 border-l-4 border-orange-500 p-3 rounded">
                        <p className="font-medium text-gray-900">{issue.title}</p>
                        <p className="text-sm text-gray-700">{issue.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {handoff.notes && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{handoff.notes}</p>
                </div>
              )}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
